import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/agreements';

// NOT: /api/agreements REST/MediatR endpoint'i (OData değil) — useC360List kullanılamaz.
// Filtre/sıralama/sayfalama client-side; backend en fazla 500 satır döndürür.
// Kayıt görüntüleme/düzenleme tam-sayfa detaya (/agreements/:id) gider — bu ekran liste-only.

const TYPE_OPTIONS = [
  { value: 'DSA', label: 'DSA' },
  { value: 'SPACEMAN', label: 'Spaceman' },
  { value: 'CONTRACT', label: 'Sözleşme' },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label]));

const PERIOD_OPTIONS = [
  { value: 'ANNUAL', label: 'Yıllık' },
  { value: 'PERIODIC', label: 'Periyodik' },
  { value: 'MONTHLY', label: 'Aylık' },
];
const PERIOD_LABEL = Object.fromEntries(PERIOD_OPTIONS.map((p) => [p.value, p.label]));

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'PENDING', label: 'Beklemede' },
  { value: 'EXPIRED', label: 'Süresi Dolmuş' },
];
const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  PENDING: { label: 'Beklemede', tone: 'wait' },
  EXPIRED: { label: 'Süresi Doldu', tone: 'bad' },
};

const COLUMN_FILTER_META = {
  title: { type: 'text', odataField: 'Title' },
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  type: { type: 'select', odataField: 'Type', options: TYPE_OPTIONS },
  period: { type: 'select', odataField: 'Period', options: PERIOD_OPTIONS },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['Title', 'CustomerTitle', 'Type'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    expiredCount: pick(data, 'expiredCount', 'ExpiredCount') ?? 0,
    pendingCount: pick(data, 'pendingCount', 'PendingCount') ?? 0,
  };
}

const typePill = (type) => {
  const label = TYPE_LABEL[type] || type || '-';
  return <span className="c360-erp-chip">{label}</span>;
};

export function AgreementsPage() {
  usePageMeta('Anlaşmalar', 'DSA / Spaceman / yıllık sözleşme master kayıtları.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  // === Veri ===
  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, expiredCount: 0, pendingCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);

  // === Arama / filtre / sıralama / sayfalama (client-side) ===
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, expiredCount: data.expiredCount, pendingCount: data.pendingCount });
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // === Client-side filtre + sıralama ===
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const searchKeys = SEARCH_FIELDS.map((f) => f.charAt(0).toLowerCase() + f.slice(1));
    const matchSearch = (row) => {
      if (!needle) return true;
      return searchKeys.some((key) => {
        const v = pick(row, key, key.charAt(0).toUpperCase() + key.slice(1));
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => {
      for (const [colId, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[colId];
        if (!meta) continue;
        const raw = pick(row, colId, colId.charAt(0).toUpperCase() + colId.slice(1));
        if (meta.type === 'select') {
          if (filter.value !== '' && filter.value != null && String(raw ?? '') !== String(filter.value)) return false;
        } else {
          const fv = (filter.value ?? '').toString().trim().toLocaleLowerCase('tr-TR');
          if (fv && !String(raw ?? '').toLocaleLowerCase('tr-TR').includes(fv)) return false;
        }
      }
      return true;
    };
    const filtered = allRows.filter((r) => matchSearch(r) && matchColumns(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'Type': return pick(row, 'type', 'Type') || '';
        case 'Period': return pick(row, 'period', 'Period') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'StartDate': return pick(row, 'startDate', 'StartDate') || '';
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((colId, next) => {
    setColumnFilters((prev) => ({ ...prev, [colId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((colId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[colId]; return copy; });
    setCurrentPage(1);
  }, []);
  const applySort = useCallback((odataField, dir) => {
    setSortField(odataField || 'UpdatedDate');
    setSortDir(dir || 'asc');
    setCurrentPage(1);
  }, []);

  // === Navigasyon (tam-sayfa detay) ===
  const goDetail = useCallback((row) => {
    if (!row) return;
    const id = pick(row, 'id', 'Id');
    if (id) navigate(`/agreements/${id}`, { state: { row } });
  }, [navigate]);
  // #282: kalem (Değiştir) düzenleme moduna açar
  const openEditRow = useCallback((row) => {
    if (!row) return;
    const id = pick(row, 'id', 'Id');
    if (id) navigate(`/agreements/${id}?edit=1`, { state: { row } });
  }, [navigate]);
  const openNew = () => navigate('/agreements/new');

  // === Kolonlar ===
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Başlık', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '(Müşteri yok)',
    }),
    columnHelper.accessor('type', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => typePill(info.getValue()),
    }),
    columnHelper.accessor('period', {
      header: 'Dönem', meta: { filterType: 'select' },
      cell: (info) => PERIOD_LABEL[info.getValue()] || info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'dates', header: 'Tarih Aralığı', enableSorting: false,
      cell: ({ row }) => {
        const start = pick(row.original, 'startDate', 'StartDate') || '?';
        const end = pick(row.original, 'endDate', 'EndDate') || '?';
        return `${start} – ${end}`;
      },
    }),
    columnHelper.accessor('autoDiscountPct', {
      header: 'İndirim', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v == null ? '-' : `%${Number(v).toFixed(2).replace(/\.00$/, '')}`; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const meta = STATUS_META[info.getValue()] || { label: info.getValue() || '-', tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); goDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); goDetail(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [goDetail, openEditRow, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Başlık', valueGetter: ({ row }) => pick(row, 'title', 'Title') || '' },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Tip', valueGetter: ({ row }) => TYPE_LABEL[pick(row, 'type', 'Type')] || pick(row, 'type', 'Type') || '' },
        { headerName: 'Dönem', valueGetter: ({ row }) => PERIOD_LABEL[pick(row, 'period', 'Period')] || pick(row, 'period', 'Period') || '' },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => pick(row, 'startDate', 'StartDate') || '' },
        { headerName: 'Bitiş', valueGetter: ({ row }) => pick(row, 'endDate', 'EndDate') || '' },
        { headerName: 'İndirim %', valueGetter: ({ row }) => { const v = pick(row, 'autoDiscountPct', 'AutoDiscountPct'); return v == null ? '' : Number(v); } },
        { headerName: 'Durum', valueGetter: ({ row }) => STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '' },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `anlasmalar_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Başlık / müşteri / tip ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Anlaşmalar"
        subtitle={`DSA / Spaceman / yıllık sözleşme master kayıtları. Aktif: ${counts.activeCount} · Beklemede: ${counts.pendingCount} · Süresi Dolmuş: ${counts.expiredCount}`}
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Anlaşma</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={goDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default AgreementsPage;
